/**
 * One filter vocabulary for every screen that lists recordings.
 *
 * Activity, the tenant dashboard, the participant dashboard and Analysis's
 * case builder each grew their own filters — a free-text box here, a plain
 * select there, four fixed date presets in one place and a list of literal
 * dates in another. The same question ("Kebebew's parathyroidectomies last
 * Tuesday afternoon") had a different answer, or no answer, depending on which
 * screen you asked it from.
 *
 * This module owns the *semantics*; `RecordingFilters.tsx` owns the controls.
 * Keeping the predicate pure and separate is what lets the rules be tested
 * once and trusted everywhere, instead of re-deriving them per screen.
 */

/** What every screen's row must expose to be filterable. Deliberately minimal
 *  — each screen maps its own shape onto this rather than sharing a model. */
export interface FilterableRecording {
  participantLabel?: string | null;
  deviceLabel?: string | null;
  procedureName?: string | null;
  /** ISO timestamp of when the recording started. */
  startedAt?: string | null;
  /** True when scoring gave up on this recording (review B-04). */
  processingFailed?: boolean | null;
}

export interface RecordingFilter {
  /** Exact participant labels to include; empty = all. */
  participants: string[];
  /** Exact device labels to include; empty = all. */
  devices: string[];
  /** Exact procedure names to include; empty = all. */
  procedures: string[];
  /** Inclusive lower bound, as a `datetime-local` string or ISO. Empty = open. */
  from: string;
  /** Inclusive upper bound. Empty = open. */
  to: string;
  /**
   * Tri-state, and optional because the third state is the common one:
   * `true` = only recordings scoring gave up on, `false` = only the ones it
   * didn't, `undefined` = no opinion.
   *
   * A boolean with a default would have collapsed two of those into one, and
   * the collapse that reads as natural — default `false`, meaning "hide the
   * failures" — is the wrong one: a recording that will never score is
   * currently indistinguishable from one still in the queue, which is the
   * whole reason this dimension exists.
   */
  processingFailed?: boolean;
}

export const EMPTY_FILTER: RecordingFilter = {
  participants: [],
  devices: [],
  procedures: [],
  from: '',
  to: '',
};

/** Presets for the common questions, so the calendar is only needed for the
 *  unusual ones. Values are resolved against `now` at click time rather than
 *  stored, so a page left open overnight doesn't silently mean yesterday. */
export type DatePreset = 'all' | 'today' | 'yesterday' | 'week' | 'month';

/** Local-time `YYYY-MM-DDTHH:mm`, the format `<input type="datetime-local">`
 *  reads and writes. Built by hand because toISOString() converts to UTC,
 *  which would shift the boundary by the viewer's offset — an 8am start
 *  becoming the previous day for anyone west of Greenwich. */
export function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** Resolve a preset into concrete bounds. `now` is injectable so the boundary
 *  behaviour is testable rather than dependent on when the suite runs. */
export function presetRange(
  preset: DatePreset,
  now: Date = new Date(),
): { from: string; to: string } {
  if (preset === 'all') return { from: '', to: '' };
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  if (preset === 'yesterday') {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  } else if (preset === 'week') {
    // Monday-anchored: a surgical week reads Mon-Sun, and "this week" on a
    // Sunday meaning "today only" surprises everyone.
    const dow = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - dow);
  } else if (preset === 'month') {
    start.setDate(1);
  }
  return { from: toLocalInput(start), to: toLocalInput(end) };
}

/** Parse a bound, tolerating both `datetime-local` and full ISO. */
function bound(value: string): number | null {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Does this recording pass the filter?
 *
 * Every dimension is AND-ed; within a dimension the selections are OR-ed —
 * "Kebebew OR lexi, doing a parathyroidectomy, last Tuesday". An empty
 * dimension means "no opinion", never "match nothing", so adding a filter can
 * only narrow.
 */
export function matchesFilter(
  r: FilterableRecording,
  f: RecordingFilter,
): boolean {
  if (f.participants.length && !f.participants.includes(r.participantLabel ?? ''))
    return false;
  if (f.devices.length && !f.devices.includes(r.deviceLabel ?? '')) return false;
  if (f.procedures.length && !f.procedures.includes(r.procedureName ?? ''))
    return false;

  if (f.processingFailed !== undefined) {
    // Coerced, because the row carries `undefined` when the screen's source
    // predates the flag; absent means "not failed", which is the truth for
    // every recording that scored before this shipped.
    if (!!r.processingFailed !== f.processingFailed) return false;
  }

  const lo = bound(f.from);
  const hi = bound(f.to);
  if (lo !== null || hi !== null) {
    // A recording with no timestamp cannot satisfy a time window. Dropping it
    // is the honest answer — showing it would claim it happened in a range
    // nobody knows it happened in.
    const at = bound(r.startedAt ?? '');
    if (at === null) return false;
    if (lo !== null && at < lo) return false;
    if (hi !== null && at > hi) return false;
  }
  return true;
}

export function filterRecordings<T extends FilterableRecording>(
  rows: readonly T[],
  f: RecordingFilter,
): T[] {
  return rows.filter((r) => matchesFilter(r, f));
}

/** Whether anything is actually narrowing — drives the "Clear" affordance. */
export function isFilterActive(f: RecordingFilter): boolean {
  return (
    f.participants.length > 0 ||
    f.devices.length > 0 ||
    f.procedures.length > 0 ||
    !!f.from ||
    !!f.to ||
    f.processingFailed !== undefined
  );
}

/** How many dimensions are narrowing — for a badge on a collapsed filter bar. */
export function activeFilterCount(f: RecordingFilter): number {
  let n = 0;
  if (f.participants.length) n += 1;
  if (f.devices.length) n += 1;
  if (f.procedures.length) n += 1;
  if (f.from || f.to) n += 1;
  if (f.processingFailed !== undefined) n += 1;
  return n;
}

/**
 * The distinct values present in the data, for the dropdowns.
 *
 * Options come from the rows themselves rather than a catalogue so the lists
 * only ever offer choices that return something — picking a procedure and
 * getting an empty screen is the fastest way to make a filter feel broken.
 */
export function filterOptions(rows: readonly FilterableRecording[]): {
  participants: string[];
  devices: string[];
  procedures: string[];
} {
  const p = new Set<string>();
  const d = new Set<string>();
  const pr = new Set<string>();
  for (const r of rows) {
    if (r.participantLabel) p.add(r.participantLabel);
    if (r.deviceLabel) d.add(r.deviceLabel);
    if (r.procedureName) pr.add(r.procedureName);
  }
  const sorted = (s: Set<string>) =>
    [...s].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return {
    participants: sorted(p),
    devices: sorted(d),
    procedures: sorted(pr),
  };
}
